import { ElementType, useCallback, useEffect, useContext } from 'react';
import { styled } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import AppMenu from '@/components/layout/AppMenu';
import Footer from '@/components/layout/Footer';
import AppBar from '@/components/layout/AppBar';
import Box from '@mui/material/Box';
import { Notification } from '@/components/Notification';
import { AppContext } from '@my-helpers/common';
import { FOOTER_HEIGHT_PX, APP_BAR_HEIGHT_PX } from '@/constants/layout';

const AppPage = styled(Box)(() => ({
	width: '100%',
	maxWidth: '100%', /* prevent child elements from exceeding the container width */
	boxSizing: 'border-box', /* to include padding in width calculations */
	wordWrap: 'break-word', /* if you have long text content that might overflow */
	overflow: 'hidden', /* Prevent horizontal scrolling */
	minHeight: 0, /* Important for flex scrolling */
	display: 'flex',
	flexDirection: 'column',
	flexGrow: 1
}));

const Layout: ElementType = ({ children }) => {
	const app = useContext(AppContext);
	if (!app) {
		throw new Error('useAppContext must be used within an AppProvider');
	}
	const { clearError, signOut, toggleTheme } = app.command;
	const { isLoading, error, sessionUser, isDarkMode } = app.view;
	const location = useLocation();
	const navigate = useNavigate();

	useEffect(() => {
		if (!sessionUser) {
			navigate('/signin', { replace: true });
		} else if (!sessionUser.emailVerified) {
			navigate('/verify', { replace: true });
		} else if (location.pathname) {
			navigate(location.pathname, { replace: true });
		} else {
			navigate('/', { replace: true });
		}
	}, [sessionUser, navigate, location.pathname]);

	return (
		<Box height="100vh" display="flex" flexDirection="column">
			<AppBar
				darkMode={isDarkMode}
				setDarkMode={toggleTheme}
				signOut={signOut}
			/>
			<Box
				display="flex"
				flexDirection="row"
				flex={1} // Take remaining space
				overflow="hidden" // Changed from "auto" to "hidden"
				minHeight={0} // Added: Important for proper flex behavior
				paddingTop={APP_BAR_HEIGHT_PX}  // Use padding instead of margin
				paddingBottom={FOOTER_HEIGHT_PX} //
			>
				<AppMenu sessionUser={sessionUser} />
				<AppPage>
					{children}
				</AppPage>
			</Box>
			<Footer />
			<Notification
				isLoading={isLoading}
				error={error}
				clearError={clearError}
			/>
		</Box>
	);
};

export default Layout;
